import React from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useCashFlow, useRegenerateCashFlow } from '@/hooks/use-financial-data';
import { EditableCFRow } from '../inline-editing/editable-cf-row';
import { updateCFLineItem, clearCFOverride } from '@/lib/api/financial';

export interface CashFlowPanelProps {
  periodId: string | undefined;
}

export const CashFlowPanel: React.FC<CashFlowPanelProps> = ({ periodId }) => {
  const { data: lineItems = [], isLoading } = useCashFlow(periodId);
  const regenerateMutation = useRegenerateCashFlow();

  const handleRegenerate = async () => {
    if (!periodId) return;
    await regenerateMutation.mutateAsync(periodId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!periodId || lineItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center border rounded-md">
        <p className="text-sm text-muted-foreground">No Cash Flow data</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Cash Flow Statement</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRegenerate}
            disabled={regenerateMutation.isPending}
          >
            {regenerateMutation.isPending ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3 mr-1" />
            )}
            Regenerate from P&L/BS
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2">Line Item</th>
              <th className="text-right py-2 w-48">Amount</th>
              <th className="text-center py-2 w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item) => (
              <EditableCFRow
                key={item.id}
                lineItem={item as any}
                lineItemLabel={item.lineItem.replace(/_/g, ' ')}
                onUpdate={async (id, data) => { await updateCFLineItem(id, data as any); }}
                onClearOverride={async (id) => { await clearCFOverride(id); }}
              />
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};
